import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {makeSizeConstants} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {UserTheme} from '../../../../libs/shared/ui/src/theme/themeTypes';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {
  isAutoGlobalCard,
  isEnhancedGlobalCard,
  isEnhancedPrivateCard,
} from '../../../helpers/models/cardHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {getCompanyName} from '../../../store/data/company/companySelectors';

/*
 * Constants.
 */

const iconSizeFromVisualSizes = makeSizeConstants(12, 14, 16);

/*
 * Props.
 */

interface ContactCardAccessIndicatorProps {
  card?: CardModel;
  isSelected?: boolean;
  iconSize?: VisualSizesEnum;
  label?: ReactNode;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-flow: column-reverse;
  align-items: flex-start;
  flex: 1 0 16px;
`;

const StyledIcon = styled(Icon)`
  height: 21px;
`;

const StyledTooltipContactSpan = styled.span`
  font-weight: ${fontWeights.bold};
  display: flex;
  align-items: center;
  gap: 6px;
  padding-bottom: 4px;
`;

const StyledContentDiv = styled.div`
  display: flex;
  gap: 4px;
`;

/*
 * Component.
 */

// defaults to rendering for auto card
export const ContactCardAccessIndicator: FC<ContactCardAccessIndicatorProps> = (props) => {
  const {card, isSelected, iconSize, label} = props;
  const theme = useUserTheme();
  const companyName = useAppSelector(getCompanyName);
  const iconName = getIconName(card);

  if (!card || !iconName) {
    return null;
  }

  const size = iconSize ? iconSizeFromVisualSizes[iconSize] : undefined;

  return (
    <StyledWrapperDiv>
      <TooltipCoordinator render={() => renderTooltip(card, companyName)} placement="top">
        <StyledContentDiv>
          <StyledIcon name={iconName} color={getIconColor(theme, isSelected)} size={size} />
          {label}
        </StyledContentDiv>
      </TooltipCoordinator>
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function renderPrivateContactTooltip() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <>
          <StyledTooltipContactSpan>
            <StyledIcon name="lockSmall" color={theme.greys.white} />
            <Trans id="contact-card-header-private-contact-tooltip-1">Private contact</Trans>
          </StyledTooltipContactSpan>
          <div>
            <Trans id="contact-card-header-private-contact-tooltip-2">
              Only you can view this contact’s details
            </Trans>
          </div>
        </>
      )}
    </UserThemeConsumer>
  );
}

function renderSharedContactTooltip(companyName: string) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <>
          <StyledTooltipContactSpan>
            <StyledIcon name="sharedFilledSmall" color={theme.greys.white} />
            <Trans id="contact-card-header-shared-contact-tooltip-1">Shared contact</Trans>
          </StyledTooltipContactSpan>
          <div>
            <Trans id="contact-card-header-shared-contact-tooltip-2">
              Everyone at {companyName} can view this contact’s details
            </Trans>
          </div>
        </>
      )}
    </UserThemeConsumer>
  );
}

function renderAutoContactTooltip() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <>
          <StyledTooltipContactSpan>
            <StyledIcon name="autoContactFilledSmall" color={theme.greys.white} />
            <Trans id="contact-card-header-auto-contact-tooltip-1">Auto-created contact</Trans>
          </StyledTooltipContactSpan>
          <div>
            <Trans id="contact-card-header-auto-contact-tooltip-2">
              Teammates who have access to conversations with this contact can view this contact’s details
            </Trans>
          </div>
        </>
      )}
    </UserThemeConsumer>
  );
}

function getIconColor(theme: UserTheme, isSelected?: boolean) {
  return isSelected ? theme.greys.white : theme.greys.shade70;
}

function renderTooltip(card: CardModel, companyName: string) {
  if (isEnhancedPrivateCard(card)) {
    return renderPrivateContactTooltip();
  }

  if (isEnhancedGlobalCard(card)) {
    return renderSharedContactTooltip(companyName);
  }

  if (isAutoGlobalCard(card)) {
    return renderAutoContactTooltip();
  }

  return null;
}

function getIconName(card?: CardModel) {
  if (!card) {
    return undefined;
  }

  if (isEnhancedPrivateCard(card)) {
    return 'lockSmall';
  }

  if (isEnhancedGlobalCard(card)) {
    return 'sharedFilledSmall';
  }

  if (isAutoGlobalCard(card)) {
    return 'autoContactFilledSmall';
  }

  return undefined;
}
